import { useEffect, useState } from 'react';
import { HiOutlineCloud, HiOutlineXCircle, HiOutlineRefresh } from 'react-icons/hi';

/**
 * Online status indicator for the header.
 * - Shows "অফলাইন" with red dot when offline
 * - Shows "অনলাইন" with green dot when online
 * - Optional pending sync count badge
 */
const OfflineIndicator = ({ pendingCount = 0, onSync }) => {
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  const handleSync = async () => {
    if (!online || !onSync || syncing) return;
    setSyncing(true);
    try {
      await onSync();
    } finally {
      setSyncing(false);
    }
  };

  if (online && pendingCount === 0) {
    return (
      <span
        className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] font-bold text-emerald-700 dark:text-emerald-400 px-2 py-1 rounded-md bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/50"
        title="ইন্টারনেট সংযুক্ত আছে"
      >
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
        </span>
        <span className="hidden sm:inline">অনলাইন</span>
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={handleSync}
      disabled={syncing}
      className={`inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] font-bold px-2 sm:px-2.5 py-1 rounded-md transition-colors ${
        online
          ? 'bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-900/20 dark:text-amber-300'
          : 'bg-rose-50 text-rose-700 dark:bg-rose-900/20 dark:text-rose-300 cursor-default'
      }`}
      title={online ? 'পেন্ডিং সিঙ্ক করতে ক্লিক করুন' : 'অফলাইন মোড — ডেটা IndexedDB তে সংরক্ষিত হচ্ছে'}
    >
      {syncing ? (
        <HiOutlineRefresh className="w-3.5 h-3.5 animate-spin" />
      ) : online ? (
        <HiOutlineCloud className="w-3.5 h-3.5" />
      ) : (
        <HiOutlineXCircle className="w-3.5 h-3.5" />
      )}

      {pendingCount > 0 && (
        <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold">
          {pendingCount}
        </span>
      )}

      <span className="hidden sm:inline">{online ? (syncing ? 'সিঙ্ক হচ্ছে...' : `সিঙ্ক (${pendingCount})`) : 'অফলাইন'}</span>
    </button>
  );
};

export default OfflineIndicator;
